import * as Sentry from "@sentry/nextjs";

/**
 * Run one callback from an analytics ready-queue, guarded.
 *
 * A ready-queue holds callbacks until its SDK can take events, then empties the
 * queue and runs them. Run bare, the first throw ends the loop and every
 * callback behind it is lost for good, because the queue is already gone. The
 * throw then leaves by whatever called the flush, which for an SDK's init is a
 * React effect: the provider unmounts to the nearest error boundary.
 *
 * `source` is one constant per queue, so a tag groups that queue's failures.
 */
export function runReadyCallback<T>(
  callback: (client: T) => void,
  client: T,
  source: string,
): void {
  try {
    callback(client);
  } catch (error) {
    // Sentry directly, not the shared logger: the logger forwards only a real
    // `Error`, and a queue callback can throw anything.
    Sentry.captureException(error, { tags: { source } });
    // Sentry has no client in local development, so without this the throw
    // this guard swallows leaves no trace where it used to reach the console.
    if (process.env.NODE_ENV !== "production") {
      console.error(`[${source}] a ready-queue callback threw`, error);
    }
  }
}
